/**
 * Recovers a patient's physical measurements from their most recent order line.
 *
 * WHY
 * ---
 * Picking a "Patient record" filled only name, age and sex, leaving weight and
 * both foot sizes blank. That is not a copy bug: the `patients` table has no
 * such columns. Weight, foot sizes, intended use and shoe brand are written
 * per-order into `order_lines.configuration`, and the only reader that
 * recovered them (`buildPatientFormFromConfig`) was reachable exclusively from
 * the order-row path. The record path had no route to them at all, and neither
 * did the prior-order modal that follows it, which passes the patient object
 * through untouched.
 *
 * WHAT HYDRATES, AND WHAT DELIBERATELY DOES NOT
 * ---------------------------------------------
 * Hydrated, because they are PHYSICAL FACTS about the person that do not
 * change with what is being ordered:
 *   - weight_kg
 *   - left/right foot size and unit
 *
 * NOT hydrated, because they are PER-ORDER DECISIONS, not properties of the
 * patient:
 *   - orthotic_intended_use  (what THIS pair is for; a running orthotic and a
 *     dress orthotic for the same foot are different prescriptions. The
 *     existing comment at PatientPicker's buildExistingPatientValue already
 *     says these "default fresh on every reorder", and that judgement stands.)
 *   - shoe_brand             (the shoe THIS pair goes into)
 *   - units                  (how many of THIS order)
 *
 * Weight is the one judgement call worth naming: it can be stale, and it feeds
 * the default shell thickness. It is hydrated anyway because a blank field is
 * not safer than a dated one (the podiatrist retypes from the same source
 * either way), but the caller is given the source order and date so the UI can
 * say where the number came from rather than presenting it as freshly measured.
 *
 * Pure and dependency-free so it can be unit-tested: PatientPicker itself
 * cannot be, because it eagerly imports @/lib/supabase.
 */

/** Mirrors PatientForm's FootSizeUnit. Redeclared rather than imported so
 *  this module stays free of the React component tree (and its eager supabase
 *  import), which is what makes it unit-testable. */
export type MetricFootUnit = 'EU' | 'UK' | 'US';

const FOOT_UNITS: readonly MetricFootUnit[] = ['EU', 'UK', 'US'];

export interface HydratedMetrics {
  weight_kg?: string;
  left_foot_size?: string;
  left_foot_unit?: MetricFootUnit;
  right_foot_size?: string;
  right_foot_unit?: MetricFootUnit;
}

export interface MetricSource {
  /** Order number the values came from, for the "as of" label. */
  orderNumber: string;
  /** ISO timestamp of that order. */
  createdAt: string;
}

export interface HydrationResult {
  metrics: HydratedMetrics;
  source: MetricSource | null;
}

/** Accepts the `{ value, unit }` shape and a bare number/string. */
function normUnit(raw: unknown): MetricFootUnit {
  // An unrecognised unit degrades to EU rather than being written through: a
  // bad unit on a foot size is a manufacturing error, not a display quirk.
  if (typeof raw !== 'string') return 'EU';
  const up = raw.trim().toUpperCase();
  return (FOOT_UNITS as readonly string[]).includes(up) ? (up as MetricFootUnit) : 'EU';
}

function footSize(raw: unknown): { value: string; unit: MetricFootUnit } | null {
  if (raw == null) return null;
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return { value: String(raw), unit: 'EU' };
  }
  if (typeof raw === 'string') {
    const t = raw.trim();
    return t ? { value: t, unit: 'EU' } : null;
  }
  if (typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const v = o.value ?? o.size;
    const u = normUnit(o.unit);
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return { value: String(v), unit: u };
    if (typeof v === 'string' && v.trim()) return { value: v.trim(), unit: u };
  }
  return null;
}

/**
 * Pull the hydratable metrics out of a line configuration.
 *
 * Every field is optional in the result: a field absent from the source is
 * LEFT ALONE by the caller rather than blanked. Overwriting a value the
 * operator already typed with an empty string would be a regression of exactly
 * the kind PR A fixed on the write side.
 */
export function metricsFromConfiguration(
  config: Record<string, unknown> | null | undefined,
): HydratedMetrics {
  if (!config) return {};
  const out: HydratedMetrics = {};

  const w = config['weight_kg'];
  if (typeof w === 'number' && Number.isFinite(w) && w > 0) out.weight_kg = String(w);
  else if (typeof w === 'string' && w.trim() && Number(w) > 0) out.weight_kg = w.trim();

  const left = footSize(config['left_foot_size']);
  if (left) {
    out.left_foot_size = left.value;
    out.left_foot_unit = left.unit;
  }
  const right = footSize(config['right_foot_size']);
  if (right) {
    out.right_foot_size = right.value;
    out.right_foot_unit = right.unit;
  }
  return out;
}

/** A line the metrics could come from. Ordered newest-first by the caller. */
export interface MetricCandidateLine {
  order_number: string;
  created_at: string;
  configuration: Record<string, unknown> | null;
}

/**
 * Take metrics from the most recent line that actually has them.
 *
 * Field-by-field rather than whole-record: a recent sandal order may carry a
 * weight but no foot size while an older insole order carries both, and the
 * podiatrist would rather have each number from the most recent order that
 * recorded it than have the whole set discarded because the newest line was
 * incomplete. `source` names the order the FIRST value came from, which is the
 * one worth showing.
 */
export function hydrateMetricsFromLines(
  lines: readonly MetricCandidateLine[],
): HydrationResult {
  const metrics: HydratedMetrics = {};
  let source: MetricSource | null = null;

  for (const line of lines) {
    const found = metricsFromConfiguration(line.configuration);
    let usedAnything = false;
    for (const [k, v] of Object.entries(found) as Array<[keyof HydratedMetrics, string]>) {
      if (metrics[k] === undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (metrics as any)[k] = v;
        // Units ride along with their size and should not, on their own, claim
        // the source order.
        if (k !== 'left_foot_unit' && k !== 'right_foot_unit') usedAnything = true;
      }
    }
    if (usedAnything && !source) {
      source = { orderNumber: line.order_number, createdAt: line.created_at };
    }
  }

  return { metrics, source };
}
